// ANGULAR
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { Component, OnDestroy, OnInit } from '@angular/core';
// SERVICES
import { AlertService } from '@services/alert.service';
import { GlobalService } from '@services/global.service';
import { ActualizacionesService } from '@api-requests-services/actualizaciones.service';
// MODELS
import { UpdateCfg, CheckRequest, PostUpdateOracleRequest, CheckRequestError } from '@models/actualizaciones.model';
// STORE
import { Store } from '@ngrx/store';
import { MainState } from '@store/main.reducer';
import { updateShowClientsRequests } from '@store/app/app.actions';
import { selectShowClientsRequests } from '@store/app/app.selectors';
// RXJS
import { Subscription } from 'rxjs';

export enum Views {
  Home = 'home',
  EditingCfg = 'editingCfg',
  CheckRequests = 'checkRequests',
  DetailCheckRequests = 'detailCheckRequests',
  CheckRequestsErrors = 'checkRequestsErrors',
}

@Component({
  selector: 'app-actualizaciones',
  templateUrl: './actualizaciones.component.html',
  styleUrl: './actualizaciones.component.css',
  imports: [
    CommonModule,
    FormsModule
  ]
})
export class ActualizacionesComponent implements OnInit, OnDestroy {
  private showClientsRequestsSubscription: Subscription = new Subscription();
  updateCfg: UpdateCfg;
  allCheckRequests: CheckRequest[];
  checkRequests: CheckRequest[];
  selectedCheckRequest: CheckRequest;
  allCheckRequestsErrors: CheckRequestError[] = [];
  checkRequestsErrors: CheckRequestError[] = [];
  // Filtros clientes
  checkRequestToSearch: string = "";
  showFilters: boolean = false;
  etrasaAPIsClientsObsoletoFilter: "all" | "unknown" | "notObsolete"| "obsolete" = "all";
  entornoFilterOptions: string[] = ["all"];
  entornoFilter: string = "all";
  machineNameFilter: string = "";
  grupoFilter: string = "all";
  grupoFilterOptions: string[] = ["all"];
  instanciaFilter: string = "";
  noActualizadaFilter: boolean = false;
  noUrlActualizarOraculoFilter: boolean = false;
  noChecksOnlineFilter: boolean = false;
  previewFilter: boolean = false;
  // Filtros errores de clientes
  checkRequestErrorToSearch: string = "";
  // Control de contenido html
  views = Views;
  currentView: string = Views.Home;

  constructor(
    private alertService: AlertService,
    public globalService: GlobalService,
    private actualizacionesService: ActualizacionesService,
    private title: Title,
    private store: Store<MainState>
  ) {
    this.title.setTitle('Oráculo - Actualizaciones');
  }

  ngOnInit(): void {
    this.getUpdatesCfg(false);

    this.showClientsRequestsSubscription = this.store.select(selectShowClientsRequests).subscribe(
      showClientsRequests => {
        if (showClientsRequests) {
          this.getCheckRequests();
        }
      }
    );
  }

  selectView(view: string, checkRequest?: CheckRequest): void {
    this.currentView = view;
    if (checkRequest) { this.selectedCheckRequest = checkRequest; }

    switch (this.currentView) {
      case Views.Home:
        this.checkRequestToSearch = '';
        this.clearFilters();
        break;
      case Views.EditingCfg:
        break;
      case Views.CheckRequests:
        break;
      case Views.DetailCheckRequests:
        break;
      case Views.CheckRequestsErrors:
        break;
    }
  }

  clearFilters(): void {
    this.etrasaAPIsClientsObsoletoFilter = "all";
    this.entornoFilter = "all";
    this.machineNameFilter = "";
    this.grupoFilter = "all";
    this.instanciaFilter = "";
    this.noActualizadaFilter = false;
    this.noUrlActualizarOraculoFilter = false;
    this.noChecksOnlineFilter = false;
    this.previewFilter = false;

    this.filterCheckRequests(true);
  }

  filterCheckRequests(clearFilters: boolean): void {
    // Filtro IP o nombre
    const search: string = this.checkRequestToSearch.trim().toLowerCase();
    this.checkRequests = search === '' ? structuredClone(this.allCheckRequests) : 
      structuredClone(this.allCheckRequests.filter(cr => cr.ip.toLowerCase().includes(search) || cr.titulo.toLowerCase().includes(search)));
    
    if (!clearFilters) {
      // Filtro EtrasaAPIsClient obsoleto
      this.checkRequests = this.checkRequests.filter(
        checkRequest => {
          switch(this.etrasaAPIsClientsObsoletoFilter) {
            case "all":
              return true;
            case "unknown":
              return checkRequest.etrasaAPIsClientsObsoleto === undefined;
            case "notObsolete":
              return checkRequest.etrasaAPIsClientsObsoleto === false;
            case "obsolete":
              return checkRequest.etrasaAPIsClientsObsoleto === true;
          }
        }
      );
      // Filtro entorno
      this.checkRequests = this.checkRequests.filter(checkRequest => this.entornoFilter === "all" ? true : checkRequest.appInfo?.entorno === this.entornoFilter);
      // Filtro nombre máquina
      this.checkRequests = this.checkRequests.filter(checkRequest => 
        this.machineNameFilter.trim() === '' ? true : checkRequest.appInfo?.machineName?.toLowerCase().includes(this.machineNameFilter.toLowerCase())
      );
      // Filtro grupo
      this.checkRequests = this.checkRequests.filter(checkRequest => this.grupoFilter === "all" ? true : checkRequest.appInfo?.grupo === this.grupoFilter);
      // Filtro instancia
      this.checkRequests = this.checkRequests.filter(checkRequest => 
        this.instanciaFilter.trim() === '' ? true : checkRequest.appInfo?.instancia?.toLowerCase().includes(this.instanciaFilter.toLowerCase())
      );
      // Filtro no actualizadas
      this.checkRequests = this.checkRequests.filter(checkRequest => this.noActualizadaFilter ? !checkRequest.actualizada : true);
      // Filtro sin url
      this.checkRequests = this.checkRequests.filter(checkRequest => this.noUrlActualizarOraculoFilter ? !checkRequest.urlActualizarOraculo : true);
      // Filtro sin checksOnline
      this.checkRequests = this.checkRequests.filter(checkRequest => this.noChecksOnlineFilter ? !checkRequest.checksOnline : true);
      // Filtro preview
      this.checkRequests = this.checkRequests.filter(checkRequest => this.previewFilter ? checkRequest.preview : true);
    }

    this.showFilters = false;
  }

  filterCheckRequestsErrors(): void {
    const search: string = this.checkRequestErrorToSearch.trim().toLowerCase();
    this.checkRequestsErrors = search === '' ? structuredClone(this.allCheckRequestsErrors) : 
      structuredClone(this.allCheckRequestsErrors.filter(cre => cre.app?.toLowerCase().includes(search) || cre.error.toLowerCase().includes(search)));
  }

  getColor(checkRequests: CheckRequest): string {
    if (checkRequests.actualizada) {
      return 'color: #009200;';
    } else {
      return 'color: var(--false-color);';
    }
  }

  getUpdatesCfg(showSwal: boolean): void {
    this.actualizacionesService.getActualizacionesCfg().subscribe(
      (updatesCfg) => {
        if (updatesCfg) {
          // console.log('Configuración de actualizaciones: ', updatesCfg);

          this.updateCfg = updatesCfg;

          if (showSwal) {
            this.alertService.showBasicMessage(`${this.updateCfg.segundosIntervalo} segundos`, ``, "success", false);
          }
        }
      },
      error => {
        if (error.error === this.globalService.editModeDisabledMsg) {
          this.alertService.swalEditingNotEnabled();
        } else {
          this.alertService.swalError(error.error);
          console.error('Error getting Updates Configuration:', error);
        }
      }
    );
  }

  setUpdatesCfg(): void {
    this.actualizacionesService.putEditActualizacionesCfg(this.updateCfg).subscribe(
      () => {
        this.alertService.showAutoCloseMessage(`Configuración de Actualizaciones editada`, ``, "success", 1200);
        this.selectView(Views.Home);
      },
      error => {
        if (error.error === this.globalService.editModeDisabledMsg) {
          this.alertService.swalEditingNotEnabled();
        } else {
          this.alertService.swalError(error.error);
          console.error('Error modifying Updates Configuration:', error);
        }
      }
    );
  }

  getCheckRequests(): void {
    this.actualizacionesService.getCheckRequests().subscribe(
      (checkRequests) => {
        if (checkRequests) {
          // console.log('Clientes: ', checkRequests);

          this.allCheckRequests = checkRequests;
          this.allCheckRequests.forEach(
            checkRequest => {
              if (!!checkRequest.appInfo?.entorno && !this.entornoFilterOptions.includes(checkRequest.appInfo.entorno)) {
                this.entornoFilterOptions.push(checkRequest.appInfo.entorno);
              }
              if (!!checkRequest.appInfo?.grupo && !this.grupoFilterOptions.includes(checkRequest.appInfo.grupo)) {
                this.grupoFilterOptions.push(checkRequest.appInfo.grupo);
              }
            }
          );
          this.filterCheckRequests(false);
          this.selectView(Views.CheckRequests);
          this.store.dispatch(updateShowClientsRequests({ showClientsRequests: false }));
        }
      },
      error => {
        this.alertService.swalError(error.error);
        console.error('Error getting Summary of Check Requests:', error);
      }
    );
  }

  deleteCheckRequests(): void {
    this.alertService.showConfirmAlert(`Resetear clientes`, `¿Seguro que quieres resetear los clientes?`).then((result) => {
      if (result.isConfirmed) {
        this.actualizacionesService.deleteCheckRequests().subscribe(
          () => {
            this.alertService.showAutoCloseMessage(`Clientes eliminados`, ``, "success", 1200);
          },
          error => {
            this.alertService.swalError(error.error);
            console.error('Error deleting clients:', error);
          }
        );
      }
    });
  }

  getCheckRequestsErrors(): void {
    this.actualizacionesService.getErroresCheckRequests().subscribe(
      (checkRequestsErrors) => {
        if (checkRequestsErrors) {
          // console.log('Errores de clientes: ', checkRequestsErrors);
          this.allCheckRequestsErrors = checkRequestsErrors;
          this.filterCheckRequestsErrors();
        }
      },
      error => {
        this.alertService.swalError(error.error);
        console.error('Error getting Summary of Check Requests:', error);
      }
    );
  }

  deleteCheckRequestsErrors(): void {
    this.alertService.showConfirmAlert(
      `Resetear errores de clientes`,
      `¿Seguro que quieres resetear los errores de clientes?`
    ).then((result) => {
      if (result.isConfirmed) {
        this.actualizacionesService.deleteErroresCheckRequests().subscribe(
          () => {
            this.alertService.showAutoCloseMessage(`Errores de clientes eliminados`, ``, "success", 1200);
          },
          error => {
            this.alertService.swalError(error.error);
            console.error('Error deleting Clients Errors:', error);
          }
        );
      }
    });
  }

  postCheckRequestsPreview(): void {
    if (!!this.selectedCheckRequest.idCliente && this.selectedCheckRequest.preview !== undefined) {
      this.actualizacionesService.postCheckRequestsPreview(this.selectedCheckRequest.idCliente, !this.selectedCheckRequest.preview).subscribe(
        () => {
          this.selectedCheckRequest.preview = !this.selectedCheckRequest.preview;
        },
        error => {
          this.alertService.swalError(error.error);
          console.error('Error modifying Check Requests Preview:', error);
        }
      );
    }
  }

  updateOracle(url: string): void {
    const postUpdateOracleRequest: PostUpdateOracleRequest = { 
      forzarLecturaOnline: true, 
      url: url 
    };

    this.actualizacionesService.postActualizarOraculo(postUpdateOracleRequest).subscribe(
      () => {
        this.alertService.showAutoCloseMessage(`Petición actualizada`, ``, "success", 1200);
        this.getCheckRequests();
      },
      error => {
        this.alertService.swalError(error.error);
        console.error('Error modifying Update Oracle:', error);
      }
    );
  }

  deleteCheckRequest(checkRequest: CheckRequest): void {
    this.alertService.showConfirmAlert(
      `Eliminar cliente`,
      `¿Seguro que quieres eliminar el cliente "${checkRequest.app}"?`
    ).then((result) => {
      if (result.isConfirmed && !!checkRequest.idCliente) {
        this.actualizacionesService.deleteCheckRequest(checkRequest.idCliente).subscribe(
          () => {
            this.getCheckRequests();
          },
          error => {
            this.alertService.swalError(error.error);
            console.error('Error deleting Summary of Check Requests:', error);
          }
        );
      }
    });
  }

  ngOnDestroy(): void {
    this.showClientsRequestsSubscription.unsubscribe();
  }
}